/* app/layouts/AppShell.jsx — корневая оболочка рабочего пространства.
   5 раскладок управляются ТОЛЬКО через CSS-токены (--sidebar-width / --tab-bar-height):
     xs   → нет сайдбара и гамбургера; нижний таб-бар + полная шторка «Ещё» (02-IA §3.5)
     sm   → сайдбар-overlay (drawer) по гамбургеру, шапка с гамбургером
     md/lg→ постоянный сайдбар слева
     xl   → постоянный сайдбар + центрированный контент (не липнет влево)
   JS лишь решает: рисовать постоянный сайдбар или drawer, и таб-бар или нет. */

import React from 'react';
import ReactDOM from 'react-dom';
import { CommandPalette } from '../CommandPalette.jsx';
import { CommandPaletteProvider } from '../CommandPaletteContext.jsx';
import { useBp } from '../providers/BreakpointProvider.jsx';
import { ChatProvider } from '../providers/ChatProvider.jsx';
import { MobileTabBar } from '../../components/patterns/MobileTabBar.jsx';
import { Sidebar } from '../../components/patterns/Sidebar.jsx';
import { OnboardingProvider } from '../onboarding/OnboardingProvider.jsx';
import { OnboardingOverlay } from '../onboarding/OnboardingOverlay.jsx';
import { Module1Overlay } from '../onboarding/module1/Module1Overlay.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { getOverlayRoot } from '../../lib/overlay.jsx';
import { RR } from '../../lib/router.jsx';

  const { useState, useEffect, createContext, useContext } = React;
  const { Outlet, useLocation } = RR;

  // ShellContext — даёт заголовкам разделов доступ к гамбургеру
  const ShellContext = createContext({ showHamburger: false, openNav: () => {} });
  export const useShell = () => useContext(ShellContext);

  // выезжающий сайдбар (sm/xs «бургер»)
  function SidebarDrawer({ open, onClose }) {
    if (!open) return null;
    return ReactDOM.createPortal(
      <div className="overlay" style={{ zIndex: 'var(--z-drawer-1)', justifyContent: 'flex-start' }}>
        <div className="overlay__backdrop" onClick={onClose} />
        <aside className="sidebar sidebar--drawer" role="dialog" aria-label="Навигация">
          <Sidebar onNavigate={onClose} />
        </aside>
      </div>,
      getOverlayRoot()
    );
  }

  // children — опциональная одиночная страница, которую нужно обернуть в оболочку
  // вне layout-роута (напр. /course/:slug для авторизованного, #16). Без children
  // (как layout-роут) поведение прежнее: рендерим <Outlet/>.
  function AppShell({ children }) {
    const { isMobile, isWide, bp } = useBp();
    const location = useLocation();
    const [navOpen, setNavOpen] = useState(false);

    // закрывать выезжающий сайдбар при смене маршрута
    useEffect(() => { setNavOpen(false); }, [location.pathname]);

    const permanentSidebar = bp === 'md' || bp === 'lg' || bp === 'xl';
    const showTabBar = bp === 'xs';
    // 02-IA §3.5: на xs — таб-бар + «Ещё» (без гамбургера); drawer/гамбургер закреплён за sm.
    const showHamburger = bp === 'sm';

    // прокидываем «гамбургер» дочерним заголовкам через CSS-переменную контекста —
    // здесь используем data-атрибут на корне и контекст показа гамбургера
    return (
      <ShellContext.Provider value={{ showHamburger, openNav: () => setNavOpen(true) }}>
        <ChatProvider>
        <CommandPaletteProvider>
        <OnboardingProvider>
        <div className={'app-shell' + (permanentSidebar ? ' app-shell--with-sidebar' : '')} data-bp={bp} data-tabbar={showTabBar ? '1' : '0'}>
          {permanentSidebar && (
            <aside className="sidebar sidebar--permanent">
              <Sidebar />
            </aside>
          )}

          <div className="app-main">
            <main className="app-content" data-scroll-main>
              {children || <Outlet />}
            </main>
          </div>
          {showTabBar && <MobileTabBar />}
          {showHamburger && <SidebarDrawer open={navOpen} onClose={() => setNavOpen(false)} />}
          <CommandPalette />
          <OnboardingOverlay />
          {/* Модуль 1 демо-CJM: квест-настройка поверх реального интерфейса */}
          <Module1Overlay />
        </div>
        </OnboardingProvider>
        </CommandPaletteProvider>
        </ChatProvider>
      </ShellContext.Provider>
    );
  }

export { AppShell };
